import type Store from '@ember-data/store';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import objectUtils from 'donor-elf-web/lib/object-utils';
import type FundsService from 'donor-elf-web/services/funds-service';
import Changeset from 'ember-changeset';
import moment from 'moment';

export default class PbtDisbursementAvailabilityController extends Controller {
  @service declare fundsService: FundsService;
  @service declare store: Store;

  @tracked report: any = null;
  @tracked month: string = '';
  @tracked year: string = '';
  @tracked loading = false;
  @tracked filterExpanded = false;
  @tracked filterChangeset?: Changeset = undefined;
  @tracked error: any = null;

  queryParams = ['month', 'year'];
  monthOptions = [
    {
      label: 'January',
      val: '1',
    },
    {
      label: 'February',
      val: '2',
    },
    {
      label: 'March',
      val: '3',
    },
    {
      label: 'April',
      val: '4',
    },
    {
      label: 'May',
      val: '5',
    },
    {
      label: 'June',
      val: '6',
    },
    {
      label: 'July',
      val: '7',
    },
    {
      label: 'August',
      val: '8',
    },
    {
      label: 'September',
      val: '9',
    },
    {
      label: 'October',
      val: '10',
    },
    {
      label: 'November',
      val: '11',
    },
    {
      label: 'December',
      val: '12',
    },
  ];

  get toDateMoment() {
    if (isEmpty(this.report)) {
      return null;
    }

    return moment({
      year: this.report.toYear,
      month: this.report.toMonth - 1,
      day: 1,
    }).endOf('month');
  }

  get actionsMenuItems() {
    return [
      {
        label: this.filterExpanded ? 'Collapse Filter' : 'Adjust Filter',
        svgJar: 'filter',
        onClick: this.toggleExpanded,
      },
    ];
  }

  get filterMessage() {
    return `${this.toDateMoment?.format('MMMM YYYY')}`;
  }

  get transactionsQueryParamsTo() {
    return this.toDateMoment?.format('YYYY-MM-DD');
  }

  get yearOptions() {
    let options = [];
    let thisYear = new Date().getFullYear();
    for (let year = thisYear - 4; year <= thisYear; year++) {
      options.push({
        label: year.toString(),
        val: year.toString(),
      });
    }
    return options;
  }

  async fetchData() {
    this.loading = true;

    const filter: any = {
      month: this.month,
      year: this.year,
      fundId: this.fundsService.selectedFund!.id,
    };

    const filterChangeset = new Changeset(filter);
    this.filterChangeset = filterChangeset;

    try {
      await this.fundsService.confirmValidFundToken();

      const records = (
        await this.store.query(
          'custom-reports/disbursement-availability-report',
          {
            filter: objectUtils.decamelizeKeys(filter),
          },
        )
      ).slice();

      console.log('Fetching disbursement availability report', records);
      this.report = records[0];
    } catch (error) {
      this.error = error;
    } finally {
      this.loading = false;
    }
  }

  applyFilter = () => {
    let filterChangeset = this.filterChangeset;

    if (filterChangeset.get('isPristine')) {
      alert('No filter values have changed');
      return;
    }

    this.month = filterChangeset.get('month');
    this.year = filterChangeset.get('year');
    this.filterExpanded = false;

    this.fetchData();
  };

  templateDidInsert = () => {
    this.month = (new Date().getMonth() + 1).toString();
    this.year = new Date().getFullYear().toString();

    this.fetchData();
  };

  toggleExpanded = () => {
    this.filterExpanded = !this.filterExpanded;
  };
}
